import { collection, getDocs, orderBy, query, where } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { k, useCss } from 'kremling'
import { isEmpty, sortBy } from 'lodash'
import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { DatePicker } from '@components/date-picker.component'
import { Loader } from '@components/loader.component'
import { Button } from '@components/mantine/button.component'
import { GroupedSelect } from '@components/mantine/grouped-select.component'
import { Select } from '@components/mantine/select.component'
import { TextInput } from '@components/mantine/text-input.component'
import { Modal } from '@components/modal.component'
import { Required } from '@components/required.component'
import { useLoad } from '@hooks/use-load.hook'
import { useProcessWorkflowEndDate } from '@hooks/use-process-workflow-end-date.hook'
import { useAppState } from '@src/app.state'
import { db, functions } from '@src/firebase-app'
import { dbNames } from '@utils/constants'
import { getTodayDate, getUserFullName } from '@utils/helpers'
import { toastService } from '@utils/toast.service'

type AddWorkflowModalProps = {
  onResolve: () => void
  onReject: () => void
  orgId: string
  template?: Template
}

export function AddWorkflowModal({ onResolve, onReject, orgId, template }: AddWorkflowModalProps) {
  const user = useAppState(state => state.user)
  const [saving, setSaving] = useState(false)
  const [isGroup, setIsGroup] = useState(false)
  const [selectedName, setSelectedName] = useState(template?.name || '')
  const [selectedUser, setSelectedUser] = useState(null)
  const [selectedTemplate, setSelectedTemplate] = useState(template || null)
  const [selectedStartDate, setSelectedStartDate] = useState(null)
  const [processedEndDate, setSelectedEndDate, endDateIsGenerated, ruleMeta] =
    useProcessWorkflowEndDate(selectedTemplate, selectedTemplate?.sections || [], selectedStartDate as any)
  const isAdmin = ['owner', 'manager'].includes(user.orgRoles[orgId])
  const scope = useCss(css)

  const [
    workflowTemplates,
    _setWorkflowTemplates,
    { loading: workflowTemplatesLoading },
  ] = useLoad<Template[]>(
    [],
    async () => {
      const q = query(
        collection(db, dbNames.workflowTemplates),
        where('orgId', '==', orgId),
        where('assistant', '==', false),
      )

      const templates = await getDocs(q).then(q =>
        q.docs.map(d => ({ ...d.data(), id: d.id }) as Template),
      )
      return sortBy(templates, 'name')
    },
    [],
  )

  const [
    publishedTemplates,
    _setPublishedTemplates,
    { loading: publishedTemplatesLoading },
  ] = useLoad<Template[]>(
    [],
    () => {
      if (!isAdmin) return Promise.resolve([])
      const q = query(
        collection(db, dbNames.publishedTemplates),
        where('orgId', '==', orgId),
        where('publishedLocations', 'array-contains', 'workspace'),
      )
      return getDocs(q).then(snap =>
        snap.docs.map(d => ({ ...d.data(), id: d.id })),
      )
    },
    [],
  )

  const [users, _setUsers, { loading: usersLoading }] = useLoad<Record<string, any>[]>(
    [],
    () => {
      const q = query(
        collection(db, dbNames.users),
        orderBy(`orgRoles.${orgId}`),
      )
      return getDocs(q)
        .then(snap =>
          snap.docs.map(d => {
            const user = { id: d.id, ...d.data() }
            return {
              ...user,
              name: getUserFullName(user),
            }
          }),
        )
        .then(users => users.filter(u => u.id !== user.id))
    },
    [],
  )
  const [groups, _setGroups, { loading: groupsLoading }] = useLoad<Record<string, any>[]>([], () => {
    const q = query(collection(db, dbNames.groups), where('orgId', '==', orgId))
    return getDocs(q).then(snap =>
      snap.docs.map(d => ({ ...d.data(), id: d.id })),
    )
  })

  const templateHasEmptySection = useMemo(() => {
    if (!selectedTemplate) return
    const { sections } = selectedTemplate
    return sections?.some((section: TemplateSection) => {
      return !section.fields.length
    })
  }, [selectedTemplate])

  // All templates combined for lookup
  const allTemplates = useMemo(
    () => [...publishedTemplates, ...workflowTemplates],
    [publishedTemplates, workflowTemplates],
  )

  // Grouped template data for admin users
  const groupedTemplateData = useMemo(
    () => [
      {
        group: 'Published',
        icon: 'globe-stand',
        items: publishedTemplates.map(t => ({ value: t.id, label: t.name })),
      },
      {
        group: 'Unpublished',
        icon: 'ballot',
        items: workflowTemplates.map(t => ({ value: t.id, label: t.name })),
      },
    ],
    [publishedTemplates, workflowTemplates],
  )

  // Simple template data for non-admin users
  const simpleTemplateData = useMemo(
    () => workflowTemplates.map(t => ({ value: t.id, label: t.name })),
    [workflowTemplates],
  )

  // All users and groups combined for lookup
  const allUsersAndGroups = useMemo(
    () => [...groups, ...users],
    [groups, users],
  )

  // Grouped user/group data
  const groupedUserData = useMemo(
    () => [
      {
        group: 'Groups',
        icon: 'users',
        items: groups.map(g => ({ value: g.id, label: g.name })),
      },
      {
        group: 'Users',
        icon: 'user',
        items: users.map(u => ({ value: u.id, label: u.name })),
      },
    ],
    [groups, users],
  )

  async function build() {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const startDate = (selectedStartDate as any).toISO({ includeOffset: false })
    const endDate = (processedEndDate as any).toISO({ includeOffset: false })
    const data = {
      startDate: {
        date: startDate,
        timeZone,
      },
      endDate: {
        date: endDate,
        timeZone,
      },
      userTimeZone: timeZone,
      selectedTemplate,
      ruleMeta,
      selectedName,
      orgId,
      today: getTodayDate().toISO(),
    }
    setSaving(true)
    try {
      const createActivity = httpsCallable(
        functions,
        'activityFunctions-createActivity',
      )
      const userIds = isGroup ? selectedUser.userIds : [selectedUser.id]

      await createActivity({ ...data, userIds })

      onResolve()
    } catch (err) {
      toastService.error('There was a problem assigning the activity')
      setSaving(false)
    }
  }

  function disableSave() {
    return (
      !selectedUser ||
      !selectedTemplate ||
      !selectedStartDate ||
      !processedEndDate ||
      saving
    )
  }

  const isLoading =
    saving ||
    workflowTemplatesLoading ||
    usersLoading ||
    groupsLoading ||
    publishedTemplatesLoading

  return (
    <>
      <Modal.Header>Assign Workflow</Modal.Header>
      <Modal.Body {...scope}>
        <div className="row break-1">
          <div className="col-6">
            <div className="form-group">
              <label htmlFor="workflow-select">
                Workflow Template <Required />
              </label>
              {isAdmin && !isEmpty(publishedTemplates) ? (
                <GroupedSelect
                  data={groupedTemplateData}
                  onChange={templateId => {
                    const t = allTemplates.find(t => t.id === templateId)
                    if (selectedTemplate) {
                      setSelectedStartDate(null)
                      setSelectedEndDate(null)
                    }
                    if (
                      !selectedName ||
                      selectedName === selectedTemplate?.name
                    ) {
                      setSelectedName(t?.name || '')
                    }
                    setSelectedTemplate(t || null)
                  }}
                  value={selectedTemplate?.id}
                  searchable
                  fullWidth
                  placeholder="Select template"
                />
              ) : (
                <Select
                  data={simpleTemplateData}
                  onChange={templateId => {
                    const t = workflowTemplates.find(t => t.id === templateId)
                    if (selectedTemplate) {
                      setSelectedStartDate(null)
                      setSelectedEndDate(null)
                    }
                    if (
                      !selectedName ||
                      selectedName === selectedTemplate?.name
                    ) {
                      setSelectedName(t?.name || '')
                    }
                    setSelectedTemplate(t || null)
                  }}
                  value={selectedTemplate?.id}
                  searchable
                  fullWidth
                  placeholder="Select template"
                />
              )}
            </div>
          </div>
          <div className="col-6">
            <div className="form-group">
              <label>
                Name <Required />
              </label>
              <TextInput
                value={selectedName}
                onChange={setSelectedName}
                disabled={!selectedTemplate}
                placeholder="Workflow name"
              />
            </div>
          </div>
        </div>
        <div className="row break-1">
          <div className="col-12">
            <div className="form-group">
              <label htmlFor="user-select">
                Assign to <Required />
              </label>
              <GroupedSelect
                data={groupedUserData}
                placeholder="Select Assignee"
                onChange={userOrGroupId => {
                  const userOrGroup = allUsersAndGroups.find(
                    u => u.id === userOrGroupId,
                  )
                  setIsGroup(!!userOrGroup?.userIds)
                  setSelectedUser(userOrGroup)
                }}
                value={selectedUser?.id}
                searchable
                fullWidth
              />
            </div>
          </div>
        </div>
        <div className="row break-1">
          <div className="col-6">
            <div className="form-group">
              <label>
                Start date <Required />
              </label>
              <DatePicker
                placeholder="Select date"
                onChange={setSelectedStartDate}
                value={selectedStartDate}
                fixedContent
                triggerIsBlock
                disabled={!selectedTemplate}
              />
            </div>
          </div>
          <div className="col-6">
            <div className="form-group">
              {endDateIsGenerated ? (
                <label>Generated end date</label>
              ) : (
                <label>
                  End date <Required />
                </label>
              )}
              <DatePicker
                placeholder={
                  endDateIsGenerated ? 'Select a start date' : 'Select date'
                }
                onChange={setSelectedEndDate as any}
                value={processedEndDate as any}
                triggerIsBlock
                fixedContent
                disabled={
                  !selectedStartDate || endDateIsGenerated || !selectedTemplate
                }
                minDate={selectedStartDate?.plus({ days: 1 }).set({
                  hour: 0,
                  minute: 0,
                  second: 0,
                  millisecond: 0,
                })}
              />
            </div>
          </div>
        </div>
        {endDateIsGenerated && (
          <div className="form-group">
            <strong>Note:</strong> This template contains section rules that
            automatically determine the end date based off of the start date.
          </div>
        )}
        {templateHasEmptySection && (
          <div className="form-group">
            <strong>Warning:</strong> This template contains at least one empty
            section. Empty sections will be marked as complete.{' '}
            <Link
              onClick={() => onReject()}
              to={`templates/${selectedTemplate.id}`}
            >
              Go to template
            </Link>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer {...scope}>
        <Button
          variant="tertiary"
          onClick={onReject}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={build}
          disabled={disableSave()}
        >
          Assign
        </Button>
      </Modal.Footer>
      {isLoading && (
        <Modal.Overlay>
          <Loader />
          <div>Assigning activity...</div>
        </Modal.Overlay>
      )}
    </>
  )
}

const css = k`
  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(#fff, .9);
    border-radius: $base-border-radius;
    flex-direction: column;
  }

  .loading-progress {
    width: 20rem;
    padding: 1.6rem;
  }
`
